import type { ErrorComponentProps } from "@tanstack/react-router";
import { useRouter } from "@tanstack/react-router";
import { PrimaryButton } from "@/components/PrimaryButton";
import { ErrorButton } from "@/components/ErrorButton";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { ChevronLeft, Copy, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import { isErrorPageAtom } from "@/lib/atoms";
import { DefaultButton } from "./DefaultButton";

export function PixelErrorFallback({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [copied, setCopied] = useState(false);
  const setIsErrorPage = useSetAtom(isErrorPageAtom);

  useEffect(() => {
    setIsErrorPage(true);
    return () => setIsErrorPage(false);
  }, [setIsErrorPage]);

  const handleCopy = () => {
    const text = error.stack || error.message;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div
      className={cn(
        "relative h-[100svh] text-white w-full flex flex-col overflow-x-hidden",
      )}
    >
      <div
        className={cn(
          "relative flex-1 mx-auto w-full flex flex-col overflow-x-hidden bg-[#1D2A52]",
          isDesktop &&
            "rounded-xl ring-[3px] border-b-[3px] ring-black border-black",
        )}
      >
        <FloatingLeftButton
          onClick={() => router.navigate({ to: "/home" })}
          className="absolute left-0 top-4 z-50"
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />

        <div className="flex flex-1 flex-col items-center justify-center gap-4 mx-4 text-center">
          <h6
            className="text-5xl text-[#cd4223]"
            style={{ fontFamily: "DeadRevolverDisplay" }}
          >
            Oops!
          </h6>

          <img src="/assets/404.png" className="h-40 w-auto" alt="404" />

          <div className="text-base text-center flex flex-col gap-1">
            <p>Looks like something went wrong on our end</p>
            <p className="text-xs opacity-60">
              You can copy the error below and open a ticket on our{" "}
              <a
                href="https://discord.com/invite/GQuP7ekfF9"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#109ffe]"
              >
                Discord
              </a>{" "}
              to report the problem
            </p>
          </div>

          <div className="relative w-full overflow-auto rounded-md bg-black/40 p-4 text-left">
            <button
              type="button"
              onClick={handleCopy}
              className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded bg-white/10 hover:bg-white/20 transition-colors hover:cursor-pointer"
            >
              {copied ? (
                <>
                  <Check size={12} className="text-green-400" />
                  <span className="text-[10px] text-green-400">Copied!</span>
                </>
              ) : (
                <>
                  <Copy size={12} className="opacity-60" />
                  <span className="text-[10px] opacity-60">Copy Error</span>
                </>
              )}
            </button>
            <p className="mt-4 mb-2 font-mono text-xs text-[#cd4223]">
              {error.message}
            </p>
            {import.meta.env.DEV && error.stack && (
              <pre className="max-h-48 overflow-auto font-mono text-[10px] leading-relaxed opacity-60">
                {error.stack}
              </pre>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <DefaultButton
              className="w-32"
              onClick={() => router.navigate({ to: "/home" })}
            >
              Go Back{" "}
            </DefaultButton>
          </div>
        </div>
      </div>
    </div>
  );
}
